import React from "react";
import { Link } from "react-router-dom";
import {
  Diamond, ArrowRight, Globe, ShieldCheck, Cpu, Lock, TrendingUp, Wallet,
} from "lucide-react";

const HEAD = { fontFamily: "'Outfit', sans-serif" };
const BODY = { fontFamily: "'Manrope', sans-serif" };

const trust = [
  { Icon: Globe, tint: "#00E5FF", title: "Global Matrix", desc: "190+ Countries Connected" },
  { Icon: ShieldCheck, tint: "#D4AF37", title: "Absolute Security", desc: "BEP20 USDT Encrypted" },
  { Icon: Cpu, tint: "#0055FF", title: "100% Code-Driven", desc: "No Human Intervention" },
];

const steps = [
  { Icon: Wallet, title: "Deposit BEP20 USDT", desc: "Secure your position with a single encrypted transfer." },
  { Icon: Globe, title: "Build a Global Network", desc: "Expand across borders — no currency barriers, no limits." },
  { Icon: TrendingUp, title: "Earn Automated Payouts", desc: "Global Club, royalties & team income — all driven by code." },
];

export default function Landing() {
  return (
    <div className="min-h-screen text-white antialiased" style={{ ...BODY, background: "#05070D" }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 h-20 flex items-center backdrop-blur-xl bg-[#05070D]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <Diamond size={22} className="text-[#D4AF37]" />
            <span className="text-xl md:text-2xl tracking-tight" style={HEAD}>LT Global Club</span>
          </Link>
          <nav className="flex items-center gap-4 md:gap-6 text-sm">
            <Link to="/login" data-testid="header-signin-btn" className="text-gray-300 hover:text-white transition-colors">Sign in</Link>
            <Link
              to="/register"
              data-testid="header-join-btn"
              className="bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] text-black font-semibold px-5 md:px-6 py-2.5 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300"
              style={HEAD}
            >
              Join the Club
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
        <div className="absolute -left-40 top-1/4 w-[36rem] h-[36rem] rounded-full bg-[#0055FF]/20 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute -right-32 bottom-0 w-[30rem] h-[30rem] rounded-full bg-[#D4AF37]/10 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:26px_26px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)] pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-center w-full">
          <div className="md:col-span-7" data-testid="hero-text">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-6">
              <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" /> SDE Program Active
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02] font-medium" style={HEAD}>
              Borderless Wealth.<br />
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] bg-clip-text text-transparent">Automated Precision.</span>
            </h1>
            <p className="mt-7 max-w-xl text-gray-300 text-base md:text-lg leading-relaxed">
              A global syndicate governed entirely by code. We merge borderless crypto networks
              with automated payout systems — ensuring absolute safety and unhindered growth.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                data-testid="hero-member-btn"
                className="bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] text-black font-semibold px-8 py-3.5 inline-flex items-center justify-center gap-2 hover:shadow-[0_0_24px_rgba(212,175,55,0.45)] transition-all duration-300"
                style={HEAD}
              >
                Become a Member <ArrowRight size={18} />
              </Link>
              <Link
                to="/login"
                data-testid="hero-account-btn"
                className="border border-[#00E5FF]/40 text-white px-8 py-3.5 inline-flex items-center justify-center hover:bg-[#00E5FF]/10 transition-all duration-300"
                style={HEAD}
              >
                I have an Account
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-gray-500">
              <span className="inline-flex items-center gap-2"><Lock size={14} className="text-[#00E5FF]" /> Non-custodial by design</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck size={14} className="text-[#D4AF37]" /> Transparent ledger</span>
            </div>
          </div>

          <div className="md:col-span-5 relative flex justify-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] aspect-square rounded-full bg-[radial-gradient(circle,rgba(0,229,255,0.35),transparent_65%)] blur-2xl pointer-events-none" aria-hidden="true" />
            <img
              src="/hero-globe.png"
              alt="A hand holding a glowing connected globe"
              className="relative z-10 w-full max-w-md object-contain globe-float drop-shadow-[0_20px_60px_rgba(0,85,255,0.35)]"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Trust & Global Presence */}
      <section className="py-14 border-y border-white/5 bg-[#0C111D]/80" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {trust.map((t) => (
            <div key={t.title} className="flex items-center gap-4" data-testid={`trust-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="shrink-0 w-12 h-12 rounded-full grid place-items-center border border-white/10" style={{ background: `${t.tint}1a` }}>
                <t.Icon size={22} style={{ color: t.tint }} />
              </div>
              <div>
                <div className="text-lg" style={HEAD}>{t.title}</div>
                <div className="text-sm text-gray-400">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission & Vision (Bento) */}
      <section className="py-24 md:py-32" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Why We Exist</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-12 max-w-2xl" style={HEAD}>
            A new formula for the world, written in code.
          </h2>
          <div className="grid md:grid-cols-12 gap-6">
            <div
              className="md:col-span-7 relative bg-[#0C111D] border border-white/5 p-8 md:p-12 overflow-hidden group hover:-translate-y-1 hover:border-[#00E5FF]/30 transition-all duration-300"
              data-testid="mission-section"
            >
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/60 to-transparent" />
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:22px_22px] opacity-60 pointer-events-none" />
              <div className="relative">
                <Globe className="text-[#00E5FF] mb-5" size={28} />
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#00E5FF] mb-3">Our Mission</div>
                <p className="text-gray-200 text-lg md:text-xl leading-relaxed" style={HEAD}>
                  We want to give a new formula to MLM business with SDE Program. To connect
                  people around the world with one another and empower the life of millions of
                  individuals by providing financial help.
                </p>
              </div>
            </div>

            <div
              className="md:col-span-5 relative bg-[#0C111D] border border-white/5 p-8 md:p-12 overflow-hidden group hover:-translate-y-1 hover:border-[#D4AF37]/30 transition-all duration-300"
              data-testid="vision-section"
            >
              <div className="absolute -bottom-16 -right-10 w-64 h-64 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <ShieldCheck className="text-[#D4AF37] mb-5" size={28} />
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Our Vision</div>
                <p className="text-gray-200 text-lg md:text-xl leading-relaxed" style={HEAD}>
                  We want to bring the world together without any currency barriers and border.
                  We aim to create a formula for passive income where no one is in control and the
                  code does everything automatically.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment of Profit */}
      <section className="py-24 md:py-32 bg-[#05070D] border-t border-white/5" data-testid="profit-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Commitment of Profit</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={HEAD}>
              Engineered for <span className="bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] bg-clip-text text-transparent">profit.</span>
            </h2>
            <p className="mt-5 text-gray-300 text-base md:text-lg leading-relaxed max-w-lg">
              The SDE Protocol operates tirelessly to generate passive income through our Global Club
              matrix and team royalties — safe, transparent, and unstoppable.
            </p>
            <Link
              to="/register"
              className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] text-black font-semibold px-8 py-3.5 hover:shadow-[0_0_24px_rgba(212,175,55,0.45)] transition-all duration-300"
              style={HEAD}
              data-testid="profit-cta"
            >
              Start Earning <ArrowRight size={18} />
            </Link>
          </div>

          <div className="space-y-4">
            {steps.map((s, i) => (
              <div
                key={s.title}
                className="flex items-start gap-5 bg-[#0C111D] border border-white/5 p-6 hover:border-[#00E5FF]/30 transition-colors duration-300"
                data-testid={`profit-step-${i + 1}`}
              >
                <div className="shrink-0 w-11 h-11 rounded-lg grid place-items-center bg-[#00E5FF]/10 border border-[#00E5FF]/20 text-[#00E5FF] font-semibold" style={HEAD}>
                  {i + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2 text-lg" style={HEAD}>
                    <s.Icon size={18} className="text-[#D4AF37]" /> {s.title}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Diamond size={18} className="text-[#D4AF37]" />
            <span style={HEAD} className="text-sm">LT Global Club</span>
          </div>
          <div className="text-xs text-gray-500">© {new Date().getFullYear()} LT Global Club — A borderless, code-driven members' platform.</div>
        </div>
      </footer>
    </div>
  );
}
